import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getArtDetail, placeOrder } from '../../services/marketplaceService';
import { resolveImageUrl, onImageError } from '../../utils/imageUrl';

export default function CheckoutPage() {
  const { artId } = useParams();

  const [art, setArt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
  });

  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      try {
        const res = await getArtDetail(artId);
        if (!cancelled) setArt(res?.data?.data?.art || null);
      } catch {
        if (!cancelled) setError('Artwork not found.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetch();
    return () => { cancelled = true; };
  }, [artId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const payload = {
        art_id: artId,
        customer_name: form.customer_name.trim(),
        customer_email: form.customer_email.trim(),
        customer_phone: form.customer_phone.trim(),
        order_type: 'sale',
      };
      const res = await placeOrder(payload);
      setSuccess(res?.data?.data);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to place order. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatPrice = (p) =>
    p != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(p) : '';

  if (loading) return <div style={{ textAlign: 'center', padding: '4rem', color: '#888' }}>Loading...</div>;

  if (success) {
    return (
        <div style={{ maxWidth: 600, margin: '4rem auto', textAlign: 'center', padding: '2rem' }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>✅</div>
          <h1 style={{ fontSize: '1.8rem', fontWeight: 700 }}>Order Placed!</h1>
          <p style={{ color: '#aaa', marginTop: '1rem' }}>
            Your order has been submitted. The artist will review it and you'll receive a payment link via email once approved.
          </p>
          <div style={{
            background: '#1a1a2e',
            borderRadius: '1rem',
            padding: '1.5rem',
            marginTop: '2rem',
            textAlign: 'left',
          }}>
            <p><strong>Order ID:</strong> {success.order_id}</p>
            <p><strong>Status:</strong> Pending Artist Approval</p>
            <p><strong>Tracking Token:</strong> <code style={{ color: '#7c3aed' }}>{success.tracking_token}</code></p>
            <p style={{ fontSize: '0.85rem', color: '#888', marginTop: '1rem' }}>
              Save your tracking token to check your order status anytime.
            </p>
          </div>
        </div>
    );
  }

  return (
      <div style={{ maxWidth: 700, margin: '0 auto', padding: '2rem 1rem' }}>
        {/* Breadcrumb */}
        <nav style={{ fontSize: '0.85rem', color: '#888', marginBottom: '1.5rem' }}>
          <Link to="/marketplace" style={{ color: '#7c3aed', textDecoration: 'none' }}>Marketplace</Link>
          <span style={{ margin: '0 0.5rem' }}>›</span>
          {art && (
            <>
              <Link to={`/marketplace/art/${art.art_id}`} style={{ color: '#7c3aed', textDecoration: 'none' }}>
                {art.title || 'Artwork'}
              </Link>
              <span style={{ margin: '0 0.5rem' }}>›</span>
            </>
          )}
          <span style={{ color: '#ccc' }}>Checkout</span>
        </nav>

        <h1 style={{ fontSize: '1.8rem', fontWeight: 700, marginBottom: '1.5rem' }}>
          Purchase Artwork
        </h1>

        {error && <div style={{ background: '#ef444433', color: '#ef4444', padding: '0.75rem 1rem', borderRadius: '0.5rem', marginBottom: '1rem' }}>{error}</div>}

        {art && (
          <div style={{ display: 'flex', gap: '1rem', background: '#1a1a2e', borderRadius: '1rem', padding: '1rem', marginBottom: '1.5rem' }}>
            <img
              src={resolveImageUrl(art.image_url)}
              alt={art.title}
              style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: '0.5rem' }}
              onError={onImageError}
            />
            <div>
              <h3 style={{ margin: 0 }}>{art.title || 'Untitled'}</h3>
              <p style={{ color: '#10b981', fontWeight: 700, fontSize: '1.2rem', margin: '0.5rem 0 0' }}>
                {formatPrice(art?.price)}
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: 600, fontSize: '0.9rem' }}>Full Name *</label>
              <input
                name="customer_name"
                value={form.customer_name}
                onChange={handleChange}
                required
                style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid #333', background: '#0f0f23', color: '#fff', fontSize: '1rem' }}
                placeholder="John Doe"
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: 600, fontSize: '0.9rem' }}>Email *</label>
              <input
                name="customer_email"
                type="email"
                value={form.customer_email}
                onChange={handleChange}
                required
                style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid #333', background: '#0f0f23', color: '#fff', fontSize: '1rem' }}
                placeholder="john@example.com"
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: 600, fontSize: '0.9rem' }}>Phone</label>
              <input
                name="customer_phone"
                type="tel"
                value={form.customer_phone}
                onChange={handleChange}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid #333', background: '#0f0f23', color: '#fff', fontSize: '1rem' }}
                placeholder="+1234567890"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            style={{
              width: '100%',
              marginTop: '1.5rem',
              padding: '1rem',
              borderRadius: '0.75rem',
              border: 'none',
              background: '#10b981',
              color: '#fff',
              fontWeight: 700,
              fontSize: '1.1rem',
              cursor: submitting ? 'wait' : 'pointer',
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? 'Placing Order...' : 'Place Order'}
          </button>

          <p style={{ textAlign: 'center', color: '#666', fontSize: '0.8rem', marginTop: '1rem' }}>
            No account needed. The artist will review your order and send you a secure payment link via email.
          </p>
        </form>
      </div>
  );
}
